/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { css, jsx } from '@emotion/react'

import { EuiIcon, EuiText, EuiSpacer, useEuiTheme } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import { privacyUrl } from '@/apps/userconsole/urls'

import ExternalLink from '../ExternalLink'

import type { FunctionComponent } from 'react'

const SupportFooter: FunctionComponent = () => {
  const { euiTheme } = useEuiTheme()

  const landingPageSupportStyle = css({
    paddingTop: '1rem',
    marginTop: 'auto',
    marginBottom: euiTheme.size.l,
    textAlign: 'center',
  })

  const landingPageElasticLogoStyle = css({
    marginTop: euiTheme.size.s,
  })

  const landingPagePrivacyStyle = css({
    color: euiTheme.colors.mediumShade,
    fontSize: 12,
    margin: `${euiTheme.size.xs} auto`,
    maxWidth: '75%',
  })

  const landingPageLinkStyle = css({
    color: `${euiTheme.colors.mediumShade}!important`,
  })

  return (
    <div css={landingPageSupportStyle}>
      <EuiText size='s'>
        <EuiIcon type='logoElastic' size='xxl' css={landingPageElasticLogoStyle} />

        <EuiSpacer size='xl' />

        <FormattedMessage
          id='landing-page.support'
          defaultMessage='Have questions? Email us at { link }'
          values={{
            link: (
              <a href='mailto:support@elastic.co?subject=Elastic%20Cloud%20Query'>
                support@elastic.co
              </a>
            ),
          }}
        />

        <EuiSpacer size='m' />

        <div css={landingPagePrivacyStyle}>
          <div>
            <FormattedMessage
              id='landing-page.links'
              defaultMessage='{ trademark } · { terms } · { privacy } · { brand }'
              values={{
                trademark: (
                  <ExternalLink
                    css={landingPageLinkStyle}
                    href='https://www.elastic.co/legal/trademarks'
                  >
                    <FormattedMessage
                      id='landing-page-privacy.trademark'
                      defaultMessage='Trademarks'
                    />
                  </ExternalLink>
                ),
                terms: (
                  <ExternalLink
                    css={landingPageLinkStyle}
                    href='https://www.elastic.co/legal/terms-of-use'
                  >
                    <FormattedMessage
                      id='landing-page-privacy.terms'
                      defaultMessage='Terms of Use'
                    />
                  </ExternalLink>
                ),
                privacy: (
                  <ExternalLink css={landingPageLinkStyle} href={privacyUrl}>
                    <FormattedMessage id='landing-page-privacy.privacy' defaultMessage='Privacy' />
                  </ExternalLink>
                ),
                brand: (
                  <ExternalLink css={landingPageLinkStyle} href='https://www.elastic.co/brand'>
                    <FormattedMessage id='landing-page-privacy.brand' defaultMessage='Brand' />
                  </ExternalLink>
                ),
              }}
            />
          </div>

          <div>
            <FormattedMessage
              id='landing-page.rights'
              defaultMessage='© 2021. All Rights Reserved – Elasticsearch'
            />
          </div>

          <div>
            <FormattedMessage
              id='landing-page.gdp3'
              defaultMessage='Elasticsearch is a trademark of Elasticsearch BV, registered in the U.S. and in other countries.'
            />
          </div>

          <div>
            <FormattedMessage
              id='landing-page.apache'
              defaultMessage='Apache, Apache Lucene, Apache Hadoop, Hadoop, HDFS and the yellow elephant logo are trademarks of the { apache } in the United States and/or other countries.'
              values={{
                apache: (
                  <ExternalLink css={landingPageLinkStyle} href='http://www.apache.org/'>
                    <FormattedMessage
                      id='landing-page-privacy.apach'
                      defaultMessage='Apache Software Foundation'
                    />
                  </ExternalLink>
                ),
              }}
            />
          </div>
        </div>
      </EuiText>
    </div>
  )
}

export default SupportFooter
